import { Application } from "../../_Bootstrap/Application";
import { closeCustomerDisplay, openCustomerDisplay } from "../../Actions/CustomerDisplay/CustomerDisplayActions";
import { type CustomerDisplayData, type CustomerDisplayItemData } from "../../Common/CustomerDisplay/CustomerDisplay";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { freshSale } from "../../Reducers/SellScreen/SellScreenSaleReducer";
import { type SaleState } from "../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import {
    type ComponentDifference,
    getComponentDifferences,
} from "../../Routes/SellScreen/Transaction/ProductLineComponents";
import { currency } from "../../Utilities/Generic/Numbers";
import { compare } from "../../Utilities/Generic/Objects";
import { getOption, subscribeToOption } from "../../Utilities/Platform/Options/Options";
import { DISPLAY_FROM_SHOPFRONT, DISPLAY_TO_SHOPFRONT } from "./DisplayEvents";
import BaseDisplayDriver, { DisplayDriverEvents, type DriverMessageData } from "./Drivers/BaseDisplayDriver";
import PopupDisplayDriver from "./Drivers/PopupDisplayDriver";
import PresentationDisplayDriver from "./Drivers/PresentationDisplayDriver";

const showApprovedPaymentMethods = [ "eftpos", "pc-eftpos", "tyro", "linkly-vaa", "custom" ];

function transformSaleState(sale: SaleState): CustomerDisplayData {
    const items = sale.products;
    const itemData: Array<CustomerDisplayItemData> = [];
    let itemCount = 0;
    let total = 0;

    for(let i = 0; i < items.length; i++) {
        const item = items[i];

        const onPromotion = !!Object.values(
            item.promotions
        ).find(promotion => promotion.active);

        let components: Array<ComponentDifference> = [];

        // The price before any components
        let price = item.prices.price;

        if(item.type === "Package") {
            components = getComponentDifferences(item.defaultProducts, item.products, item.quantity);
            price -= item.prices.additionalPrice[1];
        }

        itemData.push({
            item      : item.name + (item.isCase ? " (Case)" : ""),
            quantity  : (item.isCase ? item.quantity / item.caseQuantity : item.quantity),
            price     : currency(price / item.quantity),
            totalPrice: currency(price),
            promotion : onPromotion,
            isCase    : item.isCase,
            components,
        });

        total += item.prices.price;

        itemCount += item.quantity;
    }

    total += sale.totals.surcharge;

    let isEftpos = false;
    let cashout = 0;
    const payments = sale.payments;

    for(let i = 0; i < payments.length; i++) {
        const payment = payments[i];

        if(i === 0) {
            isEftpos = showApprovedPaymentMethods.includes(payment.type);
        }

        cashout += payment.cashout;
    }

    return {
        sale: itemData,
        text: {
            itemCount,
        },
        totals: {
            total    : currency(total),
            savings  : sale.totals.savings,
            change   : sale.totals.change,
            surcharge: sale.totals.surcharge,
            paid     : sale.totals.paid,
            remaining: sale.totals.remaining,
            cashout,
        },
        eftpos: isEftpos,
    };
}

export default class DisplayAgent {
    protected hasLoaded = false;
    protected presentationRequest!: PresentationRequest;
    protected canUsePresentation = false;
    protected mode = "";

    protected constructor() {
        this.handleDriverMessage = this.handleDriverMessage.bind(this);
        this.handleStoreChange = this.handleStoreChange.bind(this);
        this.handleTemplateChange = this.handleTemplateChange.bind(this);
        this.handleSyncFinished = this.handleSyncFinished.bind(this);
        this.init = this.init.bind(this);

        if(PresentationDisplayDriver.canUsePresentationAPI()) {
            this.presentationRequest = new PresentationRequest([ "/sell/display" ]);
            this.presentationRequest.getAvailability()
                .then(availability => {
                    this.canUsePresentation = availability.value;

                    availability.addEventListener("change", () => {
                        this.canUsePresentation = availability.value;
                    });
                });
        }

        subscribeToOption("register_customer_display", this.handleTemplateChange);

        Application.get("Redux").subscribe(this.handleStoreChange);

        document.addEventListener("synchronisationFinished", this.handleSyncFinished);

        const handleInit = () => {
            window.removeEventListener("click", handleInit);
            window.removeEventListener("keydown", handleInit);
            window.removeEventListener("touchstart", handleInit);

            return this.init(
                "register_customer_display_mode",
                subscribeToOption("register_customer_display_mode", this.init)
            );
        };

        // Create event handler to initialise the display agent when the user has interacted with the page
        window.addEventListener("click", handleInit);
        window.addEventListener("keydown", handleInit);
        window.addEventListener("touchstart", handleInit);
    }

    protected async init(_option: "register_customer_display_mode", newValue: string): Promise<void> {
        this.mode = newValue;

        if(this.displayDriver instanceof BaseDisplayDriver) {
            await this.displayDriver.close();
        }

        this.displayDriver = null;

        this.hasLoaded = false;

        const driver = await this.getDriver();

        await driver.init();

        this.hasLoaded = true;
    }

    protected handleSyncFinished(): Promise<void> {
        return this.updateCustomerDisplay(getOption("register_customer_display"));
    }

    protected handleTemplateChange(option: "register_customer_display", id: string): Promise<void> {
        return this.updateCustomerDisplay(id);
    }

    protected async updateCustomerDisplay(id: string): Promise<void> {
        try {
            const source = await determineDataSource();
            const customerDisplay = await source.customerDisplays.get(id);

            void this.sendMessage(DISPLAY_FROM_SHOPFRONT.OPEN_DISPLAY, {
                idle: customerDisplay.idle_display,
                sale: customerDisplay.sale_display,
            });

            if(this.sale) {
                void this.prepareSale(this.sale);
            }
        } catch(err) {
            if(process.env.NODE_ENV !== "test") {
                console.error("Error updating customer display template", err);
            }
        }
    }

    public async openDisplay(): Promise<boolean> {
        const results = await (await this.getDriver()).open();

        if(results) {
            this.onOpen();
        }

        return results;
    }

    protected onOpen(): void {
        Application.get("Redux").dispatch(openCustomerDisplay());
    }

    public async closeDisplay(): Promise<void> {
        await (await this.getDriver()).close();

        this.onClose();
    }

    protected onClose(): void {
        Application.get("Redux").dispatch(closeCustomerDisplay());
    }

    public async sendMessage(type: DISPLAY_FROM_SHOPFRONT, body?: unknown): Promise<void> {
        // Has not been set yet
        if(!this.hasLoaded) {
            return;
        }

        const driver = await this.getDriver();

        await driver.sendMessage(type, body);
    }

    protected prepareSale(sale: SaleState, previous = false): Promise<void> {
        return this.sendMessage(DISPLAY_FROM_SHOPFRONT.DATA_CHANGE, {
            sale: transformSaleState(sale),
            type: previous ? "previous" : "current",
        });
    }

    protected sale: SaleState | false = false;
    protected prevSale: SaleState | false = false;
    protected currentSaleId = "";
    protected upcomingSaleId = "";
    protected saleTimeout: false | ReturnType<typeof setTimeout> = false;
    protected updateSaleTimeout: undefined | ReturnType<typeof setTimeout>;
    protected hasRecentlyCompletedSale = "";
    protected previousSaleShown = "";
    protected handleStoreChange(): void {
        const state = Application.get("Redux").getState();

        if(!compare(state.sellscreen.sale, this.sale)) {
            if(this.upcomingSaleId !== state.sellscreen.sale.internalId) {
                if(this.updateSaleTimeout) {
                    clearTimeout(this.updateSaleTimeout);
                }

                this.hasRecentlyCompletedSale = state.sellscreen.sale.internalId;
            }

            if(this.currentSaleId !== state.sellscreen.sale.internalId) {
                this.hasRecentlyCompletedSale = this.currentSaleId;
                this.upcomingSaleId = state.sellscreen.sale.internalId;
                this.updateSaleTimeout = setTimeout(() => {
                    this.hasRecentlyCompletedSale = state.sellscreen.sale.internalId;
                }, 5000);
            }

            const sendSale = (): void => {
                void this.prepareSale(state.sellscreen.sale);
            };

            if(this.sale && (this.sale.products.length && !state.sellscreen.sale.products.length)) {
                if(state.sellscreen.previousSale === false || state.sellscreen.previousSale.payments.length === 0) {
                    sendSale();
                    void this.sendMessage(DISPLAY_FROM_SHOPFRONT.DISPLAY_DONE);
                } else {
                    // Check to see if the same was cancelled
                    if(this.currentSaleId !== this.hasRecentlyCompletedSale) {
                        void this.sendMessage(DISPLAY_FROM_SHOPFRONT.DISPLAY_DONE);
                        sendSale();
                    } else {
                        if(state.sellscreen.previousSale.internalId === this.previousSaleShown) {
                            void this.prepareSale(freshSale(), true);
                            void this.sendMessage(DISPLAY_FROM_SHOPFRONT.DISPLAY_DONE);
                            sendSale();
                        } else {
                            this.previousSaleShown = state.sellscreen.previousSale.internalId;
                            void this.prepareSale(state.sellscreen.previousSale);
                            void this.prepareSale(state.sellscreen.previousSale, true);
                            void this.sendMessage(DISPLAY_FROM_SHOPFRONT.DISPLAY_DONE);

                            this.saleTimeout = setTimeout(sendSale, 5000);
                        }
                    }
                }
            } else {
                if(!state.sellscreen.sale.calculate) {
                    if(this.saleTimeout) {
                        clearTimeout(this.saleTimeout);
                        this.saleTimeout = false;
                    }

                    sendSale();
                }
            }

            this.sale = state.sellscreen.sale;
            this.prevSale = state.sellscreen.previousSale;
            this.currentSaleId = state.sellscreen.sale.internalId;
        }
    }

    protected handleDriverMessage({ type, body }: DriverMessageData): void {
        switch(type) {
            case DISPLAY_TO_SHOPFRONT.DISPLAY_READY:
                this.hasLoaded = true;

                void this.handleSyncFinished();
                break;
            case DISPLAY_TO_SHOPFRONT.DISPLAY_HEARTBEAT_RESPONSE:
                // Ignore, this is handled elsewhere
                break;
            default:
                console.error("Display Agent received unknown message", type, body);
        }
    }

    protected displayDriver: BaseDisplayDriver | null = null;
    private pendingDriver: Array<() => void> = [];
    private initializing = false;
    public async getDriver(): Promise<BaseDisplayDriver> {
        // Wait for driver to be loaded
        if(this.initializing) {
            await new Promise<void>(res => {
                this.pendingDriver.push(res);
            });
        }

        if(this.displayDriver === null) {
            this.initializing = true;

            // Load the driver
            this.displayDriver = await this.determineDriver();

            this.displayDriver.addListener(DisplayDriverEvents.MESSAGE, this.handleDriverMessage);
            this.displayDriver.addListener(DisplayDriverEvents.DRIVER_CLOSE, this.onClose);
            this.displayDriver.addListener(DisplayDriverEvents.DRIVER_OPEN, this.onOpen);

            this.initializing = false;

            // Call the pending driver callbacks
            for(const callback of this.pendingDriver) {
                callback();
            }
        }

        return this.displayDriver;
    }

    protected async determineDriver(): Promise<BaseDisplayDriver> {
        if(this.canUsePresentation && this.mode === "presentation") {
            return new PresentationDisplayDriver(this.presentationRequest);
        }

        return new PopupDisplayDriver();
    }

    private static instance: DisplayAgent | undefined;
    public static getInstance(): DisplayAgent {
        if(typeof this.instance === "undefined") {
            this.instance = new DisplayAgent();
        }

        return this.instance;
    }
}
